import React, {useEffect, useState} from "react";
import {Dropdown} from "@mui/base/Dropdown";
import {MenuButton} from "@mui/base/MenuButton";
import {Menu} from "@mui/base/Menu";
import {MenuItem} from "@mui/base/MenuItem";
import CircularProgress from '@mui/material/CircularProgress';

const thumbsContainer = {
    display: "flex",
    flexDirection: "row",
    gap: "8px",
    marginTop: "12px",
    overflowX: "auto",
    flexWrap: "nowrap",
};

const thumb = {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center", // Added this to center content vertically
    borderRadius: "8px",
    border: "2px solid var(--border-strong)",
    padding: "8px 12px",
    backgroundColor: "var(--surface-subtle)",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    height: "58px",
    maxWidth: "250px",
};

const thumbImg = {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    borderRadius: "8px",
    border: "2px solid var(--border-strong)",
    backgroundColor: "var(--surface-subtle)",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    width: "58px",
    height: "58px",
};

const thumbTitle = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.875rem", // Tailwind's text-sm
    fontWeight: "500",    // Tailwind's font-medium
    color: "var(--text-subtle)",
    marginBottom: "4px",
};

const fileTypeLabel = {
    fontSize: "12px",
    color: "var(--text-secondary)",
};

const iconStyle = {
    fontSize: "1.5rem",
    display: "flex",      // Added to better center the icon
    alignItems: "center", // Added to better center the icon
    justifyContent: "center", // Added to better center the icon
    marginBottom: "4px",
};

const fileInfoContainer = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center", // This centers items vertically
    justifyContent: "space-between",
    height: "100%", // Changed from fixed height to use full available height
    gap: "16px",
};

const fileInfoContainerImg = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "58px",
    height: "58px",
};

const fileInfoContainer_2 = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center", // Changed from flex-start to center
};

const closeButton = {
    position: "absolute",
    top: "-5px",
    right: "-5px",
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid var(--border-strong)",
    background: "var(--surface-card)",
    fontSize: "16px",
    fontWeight: "bold",
    color: "var(--text-secondary)",
    cursor: "pointer",
    padding: "0",
    lineHeight: "1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
};

const img = {
    display: "block",
    width: "100%",
    height: "100%",
    borderRadius: "8px",
};

const img_pdf = {
    display: "block",
    width: "30px",
    height: "38px",
    margin: "0 auto", // Added to center the PDF icon horizontally
};

// Utility function to get file type label and icon
const getFileTypeInfo = (file) => {
    const mimeType = file?.type || "text/plain";

    if (mimeType.startsWith('image/')) {
        return {label: 'Image', icon: file.preview}; // Replace with actual icon component
    } else if (mimeType === 'application/pdf') {
        return {label: 'PDF', icon: "/document-pdf.svg"}; // Replace with actual icon component
    } else if (
        mimeType === 'application/msword' ||
        mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        mimeType === 'text/xml' ||
        mimeType === 'application/xml' ||
        mimeType === 'application/x-xml'
    ) {
        return {label: 'Document', icon: '/document-pdf.svg'}; // Replace with actual icon component
    } else {
        return {label: 'File', icon: '/document-pdf.svg'}; // Replace with actual icon component
    }
};

function AttachmentContainer({files, setFiles, isViewingUsedFiles = false}) {
    if (isViewingUsedFiles) {
        files = files.flat();
    }
    const handleRemove = (file, index) => {
        const updatedFiles = files.filter((_, i) => !(i === index && _.name === file.name));
        setFiles(updatedFiles);
    };

    const thumbs = files?.map((file, index) => {
        const safeFile = {
            ...file,
            name: file?.name || "attachment",
            type: file?.type || "text/plain",
        };
        const {label, icon} = getFileTypeInfo(safeFile);

        const isLoading = safeFile.loading;
        const isImage = safeFile.type.startsWith("image");

        return (
            <div key={index} style={isImage ? thumbImg : thumb}>
                {!isViewingUsedFiles && (
                    <button
                        onClick={() => handleRemove(safeFile, index)}
                        style={closeButton}
                        aria-label="Remove file"
                    >
                        ×
                    </button>
                )}
                <div style={isImage ? fileInfoContainerImg : fileInfoContainer}>
                    <div style={iconStyle}>
                        <img
                            src={icon}
                            style={isImage ? img : img_pdf}
                            alt={safeFile.name}
                        />
                        {isLoading && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                backgroundColor: 'color-mix(in srgb, var(--surface-card) 60%, transparent)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '8px',
                            }}>
                                <CircularProgress size={24} />
                            </div>
                        )}
                    </div>
                    {!isImage && (
                        <div style={fileInfoContainer_2}>
                            <span style={thumbTitle}>
                                {safeFile.name.length > 20 ? safeFile.name.substring(0, 18) + "..." : safeFile.name}
                            </span>
                            <span style={fileTypeLabel}>{label}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    });

    return (
        <section className="attachment-container">
            <aside style={thumbsContainer}>
                {thumbs}
            </aside>
        </section>
    );
}

export {AttachmentContainer};